<!-- A chart / table / control: the DVM Layout Builder's own build form. The
     host stays mounted (it hides itself) — the canvas runtime uses its steps. -->
<ng-container *ngIf="selectedZoneSlider() as zoneNode; else buildOrText">
  <!-- The zone slider binds straight to dataset rows (like a text box) —
       no compute payload, no Generate. -->
  <app-design-zone-slider-data
    [node]="zoneNode"
    (bindingChange)="widgetBindingChange.emit({ nodeId: zoneNode.id, binding: $event })"
    (generate)="widgetGenerate.emit($event)"
  ></app-design-zone-slider-data>
</ng-container>
<ng-template #buildOrText>
  <ng-container *ngIf="selectedButton() as buttonNode">
    <div class="ddp-head"><strong>Button</strong></div>
    <p class="ddp-hint">
      <b>{{ buttonNode.name }}</b> needs no data of its own. Pick its feature (download, API, filter, navigate, popup)
      and configure it in the <b>Design</b> tab.
    </p>
  </ng-container>
  <label class="ddp-check ddp-currency-exchange" *ngIf="selectedDropdown() as dropdownNode"
    title="The selected value is an exchange rate: the targets below are shown × that value instead of being filtered. Tick Convert currency on a table's money columns.">
    <input type="checkbox" [checked]="dropdownNode.widget?.appearance?.['currencyExchange'] === true"
      (change)="setCurrencyExchange(dropdownNode, $any($event.target).checked)" />
    Currency exchange - multiply the targets by the selected value (not a filter)
  </label>
  <label class="ddp-check ddp-value-only" *ngIf="selectedDropdown() as dropdownNode"
    title="Picking a value only shows it in the dropdown (buttons / APIs can still read it). No chart, table, slider, text or other dropdown is filtered or refreshed. Turned on by Update Configuration when no target is picked; untick it to filter the whole dashboard instead.">
    <input type="checkbox" [checked]="dropdownNode.widget?.appearance?.['valueOnly'] === true"
      (change)="setValueOnly(dropdownNode, $any($event.target).checked)" />
    Show the value only - picking a value updates nothing else (no filter, no targets)
  </label>
  <app-design-input-box-data
    *ngIf="selectedInputBox() as inputNode"
    [node]="inputNode"
    (bindingChange)="widgetBindingChange.emit({ nodeId: inputNode.id, binding: $event })"
  ></app-design-input-box-data>
  <ng-container *ngIf="!selectedButton() && selectedWidget() as widgetNode">
    <div class="ddp-head"><strong>Build</strong></div>
    <p class="ddp-hint">Same form as the DVM Layout Builder. <b>Generate</b> loads real data into <b>{{ widgetNode.name }}</b>.</p>
  </ng-container>
  <ng-container *ngIf="!selectedWidget()">
    <ng-container *ngTemplateOutlet="textData"></ng-container>
  </ng-container>
</ng-template>
<app-design-build-host [node]="buildNode()" (built)="widgetBuilt.emit($event)"></app-design-build-host>

<ng-template #textData>
<div class="ddp-head">
  <strong>Data</strong>
  <span class="ddp-head-actions">
    <button type="button" class="ddp-icon" (click)="data.refreshValues()" matTooltip="Recompute every connected text" aria-label="Reload data">
      <mat-icon>sync</mat-icon>
    </button>
    <button type="button" class="ddp-icon" (click)="reload()" matTooltip="Reload dataset list" aria-label="Reload dataset list">
      <mat-icon>refresh</mat-icon>
    </button>
  </span>
</div>

<p class="ddp-hint" *ngIf="selectedText() as text; else noText">
  Showing data in <b>{{ text.name }}</b>. Changes apply to it right away.
</p>
<ng-template #noText>
  <p class="ddp-hint">Select a text box on the canvas to show a value in it.</p>
</ng-template>

<div class="ddp-field">
  <label>Dataset</label>
  <ng-select
    #dsSelect
    class="ss-select"
    [class.is-open]="!!dsSelect.isOpen()"
    appendTo="body"
    [items]="datasetOptions()"
    [searchable]="true"
    [clearable]="true"
    [loading]="data.datasets().status === 'loading'"
    placeholder="Select a dataset"
    [ngModel]="dataset() || null"
    (ngModelChange)="onDatasetChange($event)"
  ></ng-select>
  <p class="ddp-status is-error" *ngIf="data.datasets().status === 'error'">
    {{ $any(data.datasets()).message }}
    <button type="button" class="ddp-link" (click)="reload()">Retry</button>
  </p>
</div>

<!-- A formula instead of one column: the Summary below is applied to it per
     row, e.g. SUM of ([target_price] - [set_price]). -->
<div class="ddp-field">
  <label>Formula (optional)</label>
  <input
    class="ddp-input"
    type="text"
    [disabled]="!dataset()"
    [ngModel]="isFormula() ? field() : ''"
    (change)="onFormulaChange($any($event.target).value)"
    placeholder="e.g. [target_price] - [set_price]"
  />
  <p class="ddp-status">Columns in [brackets], with + - * / and ( ). Blank = use the Column below. Press Enter to apply.</p>
</div>

<div class="ddp-field" *ngIf="!isFormula()">
  <label>Column</label>
  <ng-select
    #colSelect
    class="ss-select"
    [class.is-open]="!!colSelect.isOpen()"
    appendTo="body"
    [items]="columnOptions()"
    [searchable]="true"
    [clearable]="true"
    [disabled]="!dataset()"
    [loading]="columnState()?.status === 'loading'"
    [placeholder]="dataset() ? 'Select a column' : 'Select a dataset first'"
    [ngModel]="field() || null"
    (ngModelChange)="onFieldChange($event)"
  ></ng-select>
  <p class="ddp-status is-error" *ngIf="columnState()?.status === 'error'">{{ $any(columnState()).message }}</p>
</div>

<!-- Ticked: the text counts rows per value of the Column (STATUS_COUNTS API)
     and shows the department picked below — no Summary needed. -->
<label class="ddp-check" *ngIf="selectedText() && field() && !isFormula()">
  <input type="checkbox" [checked]="aggregation() === statusCount" (change)="onStatusCountToggle($any($event.target).checked)" />
  Count by this column's values (status counts)
</label>

<div class="ddp-field" *ngIf="aggregation() !== statusCount">
  <label>Summary</label>
  <ng-select
    #aggSelect
    class="ss-select"
    [class.is-open]="!!aggSelect.isOpen()"
    appendTo="body"
    [items]="data.aggregations()"
    bindLabel="label"
    bindValue="value"
    [clearable]="false"
    [searchable]="false"
    [disabled]="!field()"
    [ngModel]="aggregation()"
    (ngModelChange)="onAggregationChange($event)"
  ></ng-select>
</div>

<!-- Round off: the decimal places the bound value is shown with. -->
<div class="ddp-field" *ngIf="aggregation() !== statusCount && selectedText()?.dataBinding as bound">
  <label>Round off</label>
  <ng-select
    #roundSelect
    class="ss-select"
    [class.is-open]="!!roundSelect.isOpen()"
    appendTo="body"
    [items]="roundOffOptions"
    bindLabel="label"
    bindValue="value"
    [clearable]="false"
    [searchable]="false"
    [ngModel]="bound.decimals ?? autoDecimals"
    (ngModelChange)="onRoundOffChange($event)"
  ></ng-select>
</div>

<!-- Only rows where <column> is one of <values> count toward the value - e.g.
     approval_status in WAITING_FOR_APPROVAL for "oldest pending". -->
<ng-container *ngIf="aggregation() !== statusCount && selectedText()?.dataBinding as bound">
  <div class="ddp-field">
    <label>Only rows where (optional)</label>
    <ng-select
      #filterColSelect
      class="ss-select"
      [class.is-open]="!!filterColSelect.isOpen()"
      appendTo="body"
      [items]="columnOptions()"
      [searchable]="true"
      [clearable]="true"
      placeholder="All rows"
      [ngModel]="bound.filterField || null"
      (ngModelChange)="onRowFilterFieldChange($event)"
    ></ng-select>
  </div>
  <!-- Same operators as a dropdown's / table column's default filter. -->
  <div class="ddp-field" *ngIf="bound.filterField">
    <label>Operator</label>
    <ng-select
      #filterOpSelect
      class="ss-select"
      [class.is-open]="!!filterOpSelect.isOpen()"
      appendTo="body"
      [items]="rowFilterOperators"
      bindLabel="label"
      bindValue="value"
      [clearable]="false"
      [searchable]="false"
      [ngModel]="bound.filterOperator || 'in'"
      (ngModelChange)="onRowFilterOperatorChange($event)"
    ></ng-select>
  </div>
  <div class="ddp-field" *ngIf="bound.filterField && rowFilterNeedsValue(bound) && (bound.filterOperator || 'in') !== 'in'">
    <label>Value</label>
    <input
      type="text"
      class="ddp-input"
      placeholder="Value (null = empty)"
      [ngModel]="bound.filterValues?.[0] ?? ''"
      (ngModelChange)="onRowFilterValuesChange($event ? [$event] : [])"
    />
    <p class="ddp-status">Press Compute to apply.</p>
  </div>
  <div class="ddp-field" *ngIf="bound.filterField && (bound.filterOperator || 'in') === 'in'">
    <label>…is one of</label>
    <ng-select
      #filterValueSelect
      class="ss-select"
      [class.is-open]="!!filterValueSelect.isOpen()"
      appendTo="body"
      [items]="bound.filterValues ?? []"
      [multiple]="true"
      [addTag]="true"
      addTagText="Add"
      [searchable]="true"
      [clearable]="true"
      placeholder="Type a value and press Enter"
      [ngModel]="bound.filterValues ?? []"
      (ngModelChange)="onRowFilterValuesChange($event)"
    ></ng-select>
    <p class="ddp-status">Press Compute to apply.</p>
  </div>
</ng-container>

<!-- "Count per status": the Column above is the group-by column; the text
     shows the count of one status from the STATUS_COUNTS answer. -->
<ng-container *ngIf="aggregation() === statusCount && selectedText() as text">
  <div class="ddp-field">
    <label>Department / status to show</label>
    <!-- The departments the STATUS_COUNTS API answered with (loaded as soon as
         "Count per status" is chosen). Typing one that is not listed yet works too. -->
    <ng-select
      #statusSelect
      class="ss-select"
      [class.is-open]="!!statusSelect.isOpen()"
      appendTo="body"
      [items]="statusChoices()"
      [addTag]="true"
      addTagText="Use"
      [searchable]="true"
      [clearable]="true"
      [loading]="preview().text === 'Loading…'"
      placeholder="Select a department"
      [ngModel]="text.dataBinding?.statusValue || null"
      (ngModelChange)="patchTextBinding({ statusValue: $event || undefined })"
    ></ng-select>
    <p class="ddp-status" *ngIf="!statusChoices().length">Loading the departments… (or press Compute)</p>
  </div>
  <div class="ddp-field">
    <label>Count column</label>
    <input class="ddp-input" type="text" [ngModel]="text.dataBinding?.countField ?? ''"
      (ngModelChange)="patchTextBinding({ countField: $event || undefined })" placeholder="uuid" />
  </div>
</ng-container>

<!-- The text's value comes from /components/compute, like every chart, and
     recomputes with every filter. -->
<div class="ddp-field" *ngIf="canConnect()">
  <button type="button" class="ddp-connect" (click)="connect()">
    <mat-icon>bolt</mat-icon>
    Compute
  </button>
  <p class="ddp-status" *ngIf="componentId()">Component <code>{{ componentId() }}</code></p>
  <p class="ddp-status" *ngIf="!componentId()">Compute to read this value from the API.</p>
</div>

<section class="ddp-preview" *ngIf="dataset()">
  <div class="ddp-preview-value" [class.is-error]="preview().error">
    <small>Value</small>
    <strong>{{ preview().text }}</strong>
  </div>
</section>

<button type="button" class="ddp-unlink" *ngIf="selectedText()?.dataBinding" (click)="unbind.emit()">
  <mat-icon>link_off</mat-icon> Unlink from data
</button>

</ng-template>
